/**
 * BdeReport — Business Development Executive (Marketing) Report page.
 *
 * Standalone page with its own BdeReportForm (Option 2 per Phase 10C spec).
 * Uses candeza-bde-report-* testids distinct from the shared ReportForm.
 *
 * Period types: week / biweek / month / bimonth / quarter
 * 12 sections matching Rhythmnbeats businessDevExecutive form exactly.
 *
 * data-testid prefix: candeza-bde-report-
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import { PageContainer } from '@/components/layout';
import {
  useCreateStaffReport,
  useUpdateStaffReport,
  useSubmitStaffReport,
  useMyDrafts,
  useMyReports,
  useDeleteStaffReport,
  useRefillStaffReport,
  type PeriodType,
  type StaffReport,
} from '../staff/api';
import { api } from '@core/api/client';

const REPORT_TYPE = 'business-dev-executive' as const;
const PERIOD_TYPES: PeriodType[] = ['week', 'biweek', 'month', 'bimonth', 'quarter'];
const MAX_DRAFTS = 10;
const BDE_PLATFORMS = ['YouTube', 'Instagram', 'Facebook'] as const;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  gap: 0;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: none;
  border-bottom: 3px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: none;
  cursor: pointer;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.normal};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  transition: all 0.15s;
  margin-bottom: -2px;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Row = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols ?? 1}, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 0;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  min-height: 80px;
  resize: vertical;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 0;
  }
`;

const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Empty = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const DraftCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
`;

const CardInfo = styled.div`
  flex: 1;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const CardMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const CardActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

// ---------------------------------------------------------------------------
// CheckboxGroup helper
// ---------------------------------------------------------------------------

function CheckboxGroup({
  options,
  value,
  onChange,
  testIdPrefix,
}: {
  options: readonly string[];
  value: string[];
  onChange: (val: string[]) => void;
  testIdPrefix: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}-${opt.toLowerCase()}`}
            checked={value.includes(opt)}
            onChange={(e) => {
              if (e.target.checked) onChange([...value, opt]);
              else onChange(value.filter((v) => v !== opt));
            }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BdeReportForm — standalone form with candeza-bde-report-* testids
// ---------------------------------------------------------------------------

interface BdeReportFormProps {
  existingDraft?: StaffReport | null;
  draftCount?: number;
  onSaved?: () => void;
  onSubmitted?: () => void;
}

const BdeReportForm: React.FC<BdeReportFormProps> = ({
  existingDraft,
  draftCount = 0,
  onSaved,
  onSubmitted,
}) => {
  const [draftId, setDraftId] = useState<string | null>(existingDraft?.id ?? null);
  const [formData, setFormData] = useState<Record<string, unknown>>(
    existingDraft?.form_data ?? {},
  );
  const [periodType, setPeriodType] = useState<PeriodType>(
    existingDraft?.period_type ?? 'month',
  );
  const [periodStart, setPeriodStart] = useState(existingDraft?.period_start ?? '');
  const [periodEnd, setPeriodEnd] = useState(existingDraft?.period_end ?? '');
  const [submittedReport, setSubmittedReport] = useState<StaffReport | null>(null);
  const [autosaving, setAutosaving] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createMutation = useCreateStaffReport(REPORT_TYPE);
  const updateMutation = useUpdateStaffReport(REPORT_TYPE);
  const submitMutation = useSubmitStaffReport(REPORT_TYPE);

  const atDraftLimit = !draftId && draftCount >= MAX_DRAFTS;

  const get = (k: string, def: unknown = '') => formData[k] ?? def;
  const getArr = (k: string): string[] =>
    Array.isArray(formData[k]) ? (formData[k] as string[]) : [];

  const handleChange = useCallback((key: string, val: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Autosave debounce (only when draft exists)
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (!draftId) return;
    autosaveTimer.current = setTimeout(async () => {
      setAutosaving(true);
      try {
        await updateMutation.mutateAsync({
          id: draftId,
          data: { form_data: formData, period_type: periodType, period_start: periodStart, period_end: periodEnd },
        });
      } finally {
        setAutosaving(false);
      }
    }, 2000);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [formData, periodType, periodStart, periodEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPayload = () => ({
    report_type: REPORT_TYPE,
    period_type: periodType,
    period_start: periodStart,
    period_end: periodEnd,
    form_data: formData,
    status: 'draft' as const,
  });

  const handleSaveDraft = async () => {
    if (atDraftLimit) {
      toast.error(`Max ${MAX_DRAFTS} drafts reached. Discard one first.`);
      return;
    }
    if (draftId) {
      await updateMutation.mutateAsync({
        id: draftId,
        data: { form_data: formData, period_type: periodType, period_start: periodStart, period_end: periodEnd },
      });
      toast.success('Draft saved');
    } else {
      const created = await createMutation.mutateAsync(buildPayload());
      setDraftId(created.id);
      toast.success('Draft created');
    }
    onSaved?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let id = draftId;
    if (!id) {
      const draft = await createMutation.mutateAsync(buildPayload());
      id = draft.id;
      setDraftId(id);
    }
    const submitted = await submitMutation.mutateAsync(id);
    setSubmittedReport(submitted);
    toast.success('Report submitted!');
    onSubmitted?.();
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    if (!submittedReport?.pdf_ready || !submittedReport.id) {
      toast.error('PDF not ready yet. Try again shortly.');
      return;
    }
    setDownloading(true);
    try {
      const result = await api.get<{ download_url: string }>(
        `/v1/reports/staff/${REPORT_TYPE}/${submittedReport.id}/pdf`,
      );
      window.open(result.download_url, '_blank');
    } catch {
      toast.error('Failed to get PDF link.');
    } finally {
      setDownloading(false);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSubmitting = submitMutation.isPending;
  const canSubmit = !!periodStart && !!periodEnd;

  return (
    <FormWrapper onSubmit={handleSubmit} data-testid="candeza-bde-report-form" data-flow="bde-report-form">
      {atDraftLimit && (
        <div
          data-testid="candeza-bde-report-max-drafts-error"
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 6,
            padding: '10px 16px',
            color: '#92400e',
            fontSize: 13,
          }}
        >
          You have reached the maximum of {MAX_DRAFTS} drafts. Discard one to save a new report.
        </div>
      )}

      {/* Period — Section 0 */}
      <Section data-testid="candeza-bde-report-section-period">
        <SectionHeader>📅 Report Period</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Period Type</Label>
              <Select
                data-testid="candeza-bde-report-period-picker"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              >
                {PERIOD_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Period Start</Label>
              <Input
                type="date"
                data-testid="candeza-bde-report-period-start"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </Field>
            <Field>
              <Label>Period End</Label>
              <Input
                type="date"
                data-testid="candeza-bde-report-period-end"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Section 1 — WhatsApp Campaigns */}
      <Section data-testid="candeza-bde-report-section1">
        <SectionHeader>📱 WhatsApp Campaigns</SectionHeader>
        <SectionBody>
          <Field>
            <Label>WhatsApp Community Groups</Label>
            <TextArea
              data-testid="candeza-bde-report-whatsapp-campaigns"
              placeholder="List community groups messaged this period (one per line)"
              value={String(get('whatsapp_community_groups'))}
              onChange={(e) => handleChange('whatsapp_community_groups', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 2 — Social Media Campaigns */}
      <Section data-testid="candeza-bde-report-section2">
        <SectionHeader>📸 Social Media Campaigns</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Social Media Campaigns</Label>
            <TextArea
              data-testid="candeza-bde-report-social-media-campaigns"
              placeholder="Describe campaigns run on Instagram, Facebook, YouTube, etc."
              value={String(get('social_media_campaigns'))}
              onChange={(e) => handleChange('social_media_campaigns', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 3 — Physical Promotions */}
      <Section data-testid="candeza-bde-report-section3">
        <SectionHeader>🏪 Physical Promotions</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Physical Promotion Areas</Label>
            <TextArea
              data-testid="candeza-bde-report-physical-promotions"
              placeholder="Apartment complexes, schools, events visited"
              value={String(get('physical_promotion_areas'))}
              onChange={(e) => handleChange('physical_promotion_areas', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 4 — B2B Engagements */}
      <Section data-testid="candeza-bde-report-section4">
        <SectionHeader>🤝 B2B Engagements</SectionHeader>
        <SectionBody>
          <Field>
            <Label>B2B Engagements (optional)</Label>
            <TextArea
              data-testid="candeza-bde-report-b2b-engagements"
              placeholder="Corporate tie-ups, school partnerships, etc."
              value={String(get('b2b_engagements'))}
              onChange={(e) => handleChange('b2b_engagements', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 5 — Other Opportunities */}
      <Section data-testid="candeza-bde-report-section5">
        <SectionHeader>💡 Other Opportunities</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Other Opportunities (optional)</Label>
            <TextArea
              data-testid="candeza-bde-report-other-opportunities"
              placeholder="Any other marketing opportunities pursued"
              value={String(get('other_opportunities'))}
              onChange={(e) => handleChange('other_opportunities', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 6 — Community Performance */}
      <Section data-testid="candeza-bde-report-section6">
        <SectionHeader>🏘️ Community Performance</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Top 5 Communities</Label>
              <TextArea
                data-testid="candeza-bde-report-top5-communities"
                value={String(get('top5_communities'))}
                onChange={(e) => handleChange('top5_communities', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Bottom 5 Communities (optional)</Label>
              <TextArea
                data-testid="candeza-bde-report-bottom5-communities"
                value={String(get('bottom5_communities'))}
                onChange={(e) => handleChange('bottom5_communities', e.target.value)}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Section 7 — Student Spotlight Videos */}
      <Section data-testid="candeza-bde-report-section7">
        <SectionHeader>🎬 Student Spotlight Videos</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Dance</Label>
              <TextArea
                data-testid="candeza-bde-report-student-spotlight-dance"
                placeholder="Video link"
                value={String(get('student_spotlight_dance'))}
                onChange={(e) => handleChange('student_spotlight_dance', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Instruments</Label>
              <TextArea
                data-testid="candeza-bde-report-student-spotlight-instruments"
                placeholder="Video link"
                value={String(get('student_spotlight_instruments'))}
                onChange={(e) => handleChange('student_spotlight_instruments', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Vocals</Label>
              <TextArea
                data-testid="candeza-bde-report-student-spotlight-vocals"
                placeholder="Video link"
                value={String(get('student_spotlight_vocals'))}
                onChange={(e) => handleChange('student_spotlight_vocals', e.target.value)}
              />
            </Field>
          </Row>
          <Field>
            <Label>Platforms</Label>
            <CheckboxGroup
              options={BDE_PLATFORMS}
              value={getArr('spotlight_platforms')}
              onChange={(val) => handleChange('spotlight_platforms', val)}
              testIdPrefix="candeza-bde-report-spotlight-platform"
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 8 — Instructor Spotlight Videos */}
      <Section data-testid="candeza-bde-report-section8">
        <SectionHeader>🎤 Instructor Spotlight Videos</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Dance</Label>
              <TextArea
                data-testid="candeza-bde-report-instructor-spotlight-dance"
                placeholder="Video link"
                value={String(get('instructor_spotlight_dance'))}
                onChange={(e) => handleChange('instructor_spotlight_dance', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Instruments</Label>
              <TextArea
                data-testid="candeza-bde-report-instructor-spotlight-instruments"
                placeholder="Video link"
                value={String(get('instructor_spotlight_instruments'))}
                onChange={(e) => handleChange('instructor_spotlight_instruments', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Vocals</Label>
              <TextArea
                data-testid="candeza-bde-report-instructor-spotlight-vocals"
                placeholder="Video link"
                value={String(get('instructor_spotlight_vocals'))}
                onChange={(e) => handleChange('instructor_spotlight_vocals', e.target.value)}
              />
            </Field>
          </Row>
          <Field>
            <Label>Platforms</Label>
            <CheckboxGroup
              options={BDE_PLATFORMS}
              value={getArr('instructor_spotlight_platforms')}
              onChange={(val) => handleChange('instructor_spotlight_platforms', val)}
              testIdPrefix="candeza-bde-report-instructor-spotlight-platform"
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 9 — Music Room Showcase */}
      <Section data-testid="candeza-bde-report-section9">
        <SectionHeader>🎵 Music Room Showcase</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Showcase Link</Label>
              <Input
                type="text"
                data-testid="candeza-bde-report-music-room-showcase-link"
                placeholder="https://youtube.com/..."
                value={String(get('music_room_showcase_link'))}
                onChange={(e) => handleChange('music_room_showcase_link', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Showcase Date</Label>
              <Input
                type="date"
                data-testid="candeza-bde-report-music-room-showcase-date"
                value={String(get('music_room_showcase_date'))}
                onChange={(e) => handleChange('music_room_showcase_date', e.target.value)}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Section 10 — Poster Display */}
      <Section data-testid="candeza-bde-report-section10">
        <SectionHeader>🪧 Poster Display</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            {(
              [
                ['birthday_poster', 'Birthday Poster'],
                ['festivals_poster', 'Festivals Poster'],
                ['music_room_poster', 'Music Room Poster'],
                ['google_review_slideshow', 'Google Review Slideshow'],
              ] as const
            ).map(([field, labelText]) => (
              <Field key={field}>
                <Label>{labelText}</Label>
                <Select
                  data-testid={`candeza-bde-report-${field.replace(/_/g, '-')}`}
                  value={String(get(field))}
                  onChange={(e) => handleChange(field, e.target.value)}
                >
                  <option value="">— select —</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="N/A">N/A</option>
                </Select>
              </Field>
            ))}
          </Row>
        </SectionBody>
      </Section>

      {/* Section 11 — Google Page Updates */}
      <Section data-testid="candeza-bde-report-section11">
        <SectionHeader>🌐 Google Page Updates</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Facility Photos</Label>
              <Select
                data-testid="candeza-bde-report-facility-photos"
                value={String(get('facility_photos'))}
                onChange={(e) => handleChange('facility_photos', e.target.value)}
              >
                <option value="">— select —</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
            </Field>
            <Field>
              <Label>Promotion Posters</Label>
              <Select
                data-testid="candeza-bde-report-promotion-posters"
                value={String(get('promotion_posters'))}
                onChange={(e) => handleChange('promotion_posters', e.target.value)}
              >
                <option value="">— select —</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
            </Field>
          </Row>
          <Field>
            <Label>Important Announcements</Label>
            <TextArea
              data-testid="candeza-bde-report-important-announcements"
              value={String(get('important_announcements'))}
              onChange={(e) => handleChange('important_announcements', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 12 — Support Needed */}
      <Section data-testid="candeza-bde-report-section12">
        <SectionHeader>🆘 Support Needed</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Support Needed (optional)</Label>
            <TextArea
              data-testid="candeza-bde-report-support-needed"
              value={String(get('support_needed'))}
              onChange={(e) => handleChange('support_needed', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Actions */}
      <Actions>
        {autosaving && (
          <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
            Autosaving…
          </span>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSaveDraft}
          disabled={isSaving || atDraftLimit}
          data-testid="candeza-bde-report-save-btn"
          data-flow="bde-report-save-btn"
        >
          {isSaving ? 'Saving…' : '💾 Save Draft'}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting || !canSubmit}
          data-testid="candeza-bde-report-submit-btn"
          data-flow="bde-report-submit-btn"
        >
          {isSubmitting ? 'Submitting…' : '✅ Submit Report'}
        </Button>
        {submittedReport && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={!submittedReport.pdf_ready || downloading}
            data-testid="candeza-bde-report-pdf-btn"
          >
            {downloading ? 'Loading…' : '⬇ Download PDF'}
          </Button>
        )}
      </Actions>
    </FormWrapper>
  );
};

// ---------------------------------------------------------------------------
// Drafts list
// ---------------------------------------------------------------------------

const BdeDraftsList: React.FC<{ onResume: (draft: StaffReport) => void }> = ({ onResume }) => {
  const { data, isLoading } = useMyDrafts(REPORT_TYPE);
  const deleteMutation = useDeleteStaffReport(REPORT_TYPE);
  const [discarding, setDiscarding] = useState<string | null>(null);

  const handleDiscard = async (id: string) => {
    setDiscarding(id);
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Draft discarded');
    } catch {
      toast.error('Failed to discard draft');
    } finally {
      setDiscarding(null);
    }
  };

  if (isLoading) {
    return <div data-testid="candeza-bde-report-loading">Loading drafts…</div>;
  }

  const drafts = data?.items ?? [];
  if (!drafts.length) {
    return <Empty data-testid="candeza-bde-report-empty">No drafts. Start a new report.</Empty>;
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      data-testid="candeza-bde-report-drafts-list"
    >
      {drafts.map((draft) => {
        const daysLeft = Math.max(
          0,
          Math.ceil((new Date(draft.expires_at).getTime() - Date.now()) / 86_400_000),
        );
        return (
          <DraftCard key={draft.id}>
            <CardInfo>
              <CardTitle>
                {draft.period_start} – {draft.period_end} ({draft.period_type})
              </CardTitle>
              <CardMeta>
                Updated: {new Date(draft.updated_at).toLocaleDateString()} ·{' '}
                <span style={{ color: daysLeft <= 1 ? '#ef4444' : '#92400e' }}>
                  Expires in {daysLeft}d
                </span>
              </CardMeta>
            </CardInfo>
            <CardActions>
              <Button variant="outline" size="sm" onClick={() => onResume(draft)}>
                ▶ Resume
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDiscard(draft.id)}
                disabled={discarding === draft.id}
              >
                {discarding === draft.id ? '…' : '🗑 Discard'}
              </Button>
            </CardActions>
          </DraftCard>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Submitted reports list
// ---------------------------------------------------------------------------

const BdeSubmittedList: React.FC = () => {
  const { data, isLoading } = useMyReports(REPORT_TYPE);
  const refillMutation = useRefillStaffReport(REPORT_TYPE);
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = async (report: StaffReport) => {
    if (!report.pdf_ready) {
      toast.error('PDF not ready. Try again shortly.');
      return;
    }
    setDownloading(report.id);
    try {
      const result = await api.get<{ download_url: string }>(
        `/v1/reports/staff/${REPORT_TYPE}/${report.id}/pdf`,
      );
      window.open(result.download_url, '_blank');
    } catch {
      toast.error('Failed to get PDF link.');
    } finally {
      setDownloading(null);
    }
  };

  const handleRefill = async (report: StaffReport) => {
    try {
      await refillMutation.mutateAsync(report.id);
      toast.success('New draft created from this report');
    } catch {
      toast.error('Failed to create re-fill draft');
    }
  };

  if (isLoading) {
    return <div data-testid="candeza-bde-report-submitted-loading">Loading reports…</div>;
  }

  const reports = data?.items ?? [];
  if (!reports.length) {
    return (
      <Empty data-testid="candeza-bde-report-submitted-empty">No submitted reports yet.</Empty>
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      data-testid="candeza-bde-report-submitted-list"
    >
      {reports.map((report) => (
        <DraftCard key={report.id}>
          <CardInfo>
            <CardTitle>
              {report.period_start} – {report.period_end} ({report.period_type})
            </CardTitle>
            <CardMeta>
              Submitted:{' '}
              {report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : '—'} ·{' '}
              {report.pdf_ready ? '✅ PDF ready' : '⏳ PDF pending'}
            </CardMeta>
          </CardInfo>
          <CardActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload(report)}
              disabled={!report.pdf_ready || downloading === report.id}
            >
              {downloading === report.id ? 'Loading…' : '⬇ PDF'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRefill(report)}
              disabled={refillMutation.isPending}
            >
              🔁 Re-fill
            </Button>
          </CardActions>
        </DraftCard>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page — BdeReport
// ---------------------------------------------------------------------------

type TabView = 'new' | 'drafts' | 'submitted';

export const BdeReport: React.FC = () => {
  const [tab, setTab] = useState<TabView>('new');
  const [editingDraft, setEditingDraft] = useState<StaffReport | null>(null);
  const { data: draftsData } = useMyDrafts(REPORT_TYPE);
  const draftCount = draftsData?.items?.length ?? 0;

  const handleResume = (draft: StaffReport) => {
    setEditingDraft(draft);
    setTab('new');
  };

  const handleSaved = () => {
    setEditingDraft(null);
    setTab('drafts');
  };

  const handleSubmitted = () => {
    setEditingDraft(null);
    setTab('submitted');
  };

  return (
    <PageContainer
      title="Marketing Report"
      subtitle="Business Development Executive — monthly/quarterly marketing activity"
      data-testid="candeza-bde-report-page"
    >
      <TabBar data-testid="candeza-bde-report-tabs">
        <Tab
          $active={tab === 'new'}
          onClick={() => {
            setTab('new');
            setEditingDraft(null);
          }}
          data-testid="candeza-bde-report-tab-new"
        >
          ✏️ New Report
        </Tab>
        <Tab
          $active={tab === 'drafts'}
          onClick={() => setTab('drafts')}
          data-testid="candeza-bde-report-tab-drafts"
        >
          📝 Drafts ({draftCount})
        </Tab>
        <Tab
          $active={tab === 'submitted'}
          onClick={() => setTab('submitted')}
          data-testid="candeza-bde-report-tab-submitted"
        >
          📋 Submitted
        </Tab>
      </TabBar>

      {tab === 'new' && (
        <BdeReportForm
          existingDraft={editingDraft}
          draftCount={draftCount}
          onSaved={handleSaved}
          onSubmitted={handleSubmitted}
        />
      )}

      {tab === 'drafts' && (
        <BdeDraftsList onResume={handleResume} />
      )}

      {tab === 'submitted' && <BdeSubmittedList />}
    </PageContainer>
  );
};
